/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect} from 'react'
import {Routes, Route, BrowserRouter, Navigate, useLocation} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import { ToastContainer } from 'react-toastify'
import { ViewTxn } from '../modules/transactions/ViewTxn'
import { ViewPayment } from '../modules/transactions/ViewPayment'
import { brandSettings } from '../modules/settings/BrandSettings'
import { DeleteConfirmation } from '../modules/auth/components/ConfirmDeleteBiz'
import { ConfirmDeleteBranch } from '../modules/auth/components/ConfirmDeleteBranch'
import { EmailPreferences } from '../modules/auth/components/EmailPreferences'
import { DemoLanding } from '../modules/auth/components/DemoLanding'
import { AuthLayout } from '../modules/auth/AuthLayout'
import { isLoggingOut } from '../modules/auth/core/AuthHelpers'
/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {BASE_URL} = import.meta.env

function useTitle(title: string): void {
  useEffect(() => {
      document.title = title;
  }, [title]);
}

function useFavicon(faviconUrl: string): void {
  useEffect(() => {
      const link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (link) {
          link.href = faviconUrl;
      } else {
          const newLink = document.createElement("link");
          newLink.rel = "icon";
          newLink.href = faviconUrl;
          document.head.appendChild(newLink);
      }
  }, [faviconUrl]);
}

const LoginRedirect: FC = () => {
  const location = useLocation()
  const isAuthPath = location.pathname.startsWith('/auth/')
  const isRootPath = location.pathname === '/'

  // Known private route prefixes — unknown paths get a 404 even when unauthenticated
  const knownPrivatePrefixes = [
    '/dashboard', '/contacts', '/items', '/transactions', '/banking',
    '/reports', '/settings', '/profile', '/apps', '/documents',
    '/manufacturing', '/projects', '/budgets',
  ]
  const isKnownPrivatePath = knownPrivatePrefixes.some(p => location.pathname.startsWith(p))

  if (!isAuthPath && !isRootPath) {
    if (isKnownPrivatePath) {
      sessionStorage.setItem('auth_intended_url', location.pathname + location.search)
    } else if (!isLoggingOut()) {
      // A logout leaves the router at whatever private page the user was on —
      // even one not in the prefix list — so only 404 organic unknown URLs
      return <Navigate to='/error/404' replace />
    }
  }
  return <Navigate to='/auth/login' replace />
}

const AppRoutes: FC = () => {
  useTitle(`${brandSettings.displayName} Accounting Software`)
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={BASE_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path='txn/:txnType/:txnId' element={<ViewTxn publicView />} />
          <Route path='payment/:paymentType/:paymentId' element={<ViewPayment publicView />} />
          <Route element={<AuthLayout />}>
            <Route path='auth/confirm-delete' element={<DeleteConfirmation />} />
            <Route path='auth/confirm-delete-branch' element={<ConfirmDeleteBranch />} />
            <Route path='auth/email-preferences' element={<EmailPreferences />} />
            <Route path='demo' element={<DemoLanding />} />
          </Route>
          
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<LoginRedirect />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
